// INC2-ACCESS (Andrei, 2026-10-28): the access matrix. Rows are agents (DEs, and the caller's
// own personal agent, labelled AG), columns are connections (the company's, and the caller's own),
// each cell a level. No new model: a cell is the agents' `connection_grant` (use_read · use_act ·
// manage, families, limits), and "act with approval" vs "act directly" is the seat's `external`
// policy class (company ceiling → seat), read from the policy, settable only once that class is
// enforced on every write path. Least privilege: an agent with no grant has no access.
//
//   GET  access-matrix                       the grid the caller may see (filters: team, connection)
//   POST access-matrix/apply                 set cells (one, a row, a column or a team) in one go
//   GET  access-matrix/template/{id}         what a DE's template asks for, as cells to confirm
//
// Who may change a cell: a company connection's, those who manage its grants (owners and admins,
// and anyone they gave Manage on it); a person's own connection's, that person only, for the DEs
// whose seat they supervise (their own personal agent's access is set in My agent). Everyone else
// reads the grid read-only, filtered to what they may see: a person's own connections are never
// shown to anyone else. Every change is a `connection.grant_changed` event, and the grant is
// checked again on every act, at execution.
import { z } from "zod";
import { ConnectionFamily, ConnectionGrantLevel } from "../enums";
import { ConnectionGrantLimits } from "../outbox-rows";
import { Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const core = { feature: "core", tag: "connections" } as const;

/** A cell's level: none, or a grant level. */
export const AccessMatrixLevel = z.enum(["none", "use_read", "use_act", "manage"]);
export type AccessMatrixLevel = z.infer<typeof AccessMatrixLevel>;
/** How an agent's outward acts go out: approved first, or directly (the seat's `external` class). */
export const AccessActMode = z.enum(["approval", "direct"]);

export const AccessMatrixQuery = z.object({
  team: Uuid.optional(),
  connection: Uuid.optional(),
});

export const AccessMatrixRow = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    /** `de`: a digital employee; `ag`: the caller's own personal agent. */
    kind: z.enum(["de", "ag"]),
    seat_id: Uuid.nullable(),
    seat_title: z.string().nullable(),
    team_ids: z.array(Uuid),
    /** The caller supervises it (holds its seat's supervisor seat), or it is their own agent. */
    supervised: z.boolean(),
    /** How its outward acts go out today (the seat's `external` class). */
    act_mode: AccessActMode,
  })
  .meta({ id: "AccessMatrixRow" });

export const AccessMatrixColumn = z
  .object({
    connection_id: Uuid,
    label: z.string(),
    provider: z.string(),
    /** `company`: shared; `person`: the caller's own. */
    scope: z.enum(["company", "person"]),
    /** The account it is signed in as. */
    identity_display: z.string().nullable(),
    state: z.string(),
    /** The caller may change its cells (for a person's own: only on DE rows they supervise). */
    editable: z.boolean(),
  })
  .meta({ id: "AccessMatrixColumn" });

export const AccessMatrixCell = z
  .object({
    connection_id: Uuid,
    membership_id: Uuid,
    level: ConnectionGrantLevel,
    /** Where the level comes from: the agent's own grant, its seat's, a team's, or the crew's. */
    via: z.enum(["direct", "seat", "team", "crew"]),
    /** The agent's own grant (the one a change replaces), when there is one. */
    grant_id: Uuid.nullable(),
    families: z.array(ConnectionFamily),
    limits: ConnectionGrantLimits,
    /** Who made the grant the level comes from, and when. */
    changed_by_membership_id: Uuid.nullable(),
    changed_at: z.iso.datetime({ offset: true }),
    /**
     * The strongest level held through the seat, a team or the crew (null: none). A change here
     * writes only the agent's own grant, so it cannot go below this level.
     */
    inherited_level: ConnectionGrantLevel.nullable(),
    /**
     * Every live grant the agent acts under on this connection. An act may use any of them, so
     * together (not the strongest alone) they are what the agent may do.
     */
    sources: z.array(
      z.object({
        via: z.enum(["direct", "seat", "team", "crew"]),
        level: ConnectionGrantLevel,
        families: z.array(ConnectionFamily),
        limits: ConnectionGrantLimits,
      }),
    ),
  })
  .meta({ id: "AccessMatrixCell" });

export const AccessMatrix = z
  .object({
    rows: z.array(AccessMatrixRow),
    columns: z.array(AccessMatrixColumn),
    /** Only cells with access (a missing cell is no access). */
    cells: z.array(AccessMatrixCell),
    teams: z.array(z.object({ id: Uuid, name: z.string() })),
    /** Whether "act directly" can be chosen yet (the `external` class is enforced). */
    direct_available: z.boolean(),
    /** Whether the caller may set an agent's act mode (owners and admins). */
    can_set_act_mode: z.boolean(),
  })
  .meta({ id: "AccessMatrix" });

export const AccessMatrixChange = z
  .strictObject({
    connection_id: Uuid,
    membership_id: Uuid,
    level: AccessMatrixLevel,
    /** Empty or absent: every family the connection serves. */
    families: z.array(ConnectionFamily).max(5).optional(),
    limits: ConnectionGrantLimits.optional(),
  })
  .meta({ id: "AccessMatrixChange" });

export const AccessMatrixApply = z
  .strictObject({
    /** One cell, a row, a column or a team's rows: applied together or not at all. */
    changes: z.array(AccessMatrixChange).min(1).max(500),
    /** Set the changed rows' act mode too (owners and admins; only once `direct_available`). */
    act_mode: AccessActMode.optional(),
  })
  .meta({ id: "AccessMatrixApply" });

export const AccessMatrixApplyResult = z
  .object({
    /** Grants made or ended. */
    changed: z.int(),
    matrix: AccessMatrix,
  })
  .meta({ id: "AccessMatrixApplyResult" });

export const AccessMatrixTemplate = z
  .object({
    membership_id: Uuid,
    template_key: z.string().nullable(),
    /** What the template asks for, matched to the company's connections. */
    proposals: z.array(
      z.object({
        provider: z.string(),
        level: ConnectionGrantLevel,
        families: z.array(ConnectionFamily),
        /** Why the template needs it (its own words), when given. */
        why: z.string().nullable(),
        /** The company connection it would use; null: none of that provider is connected. */
        connection_id: Uuid.nullable(),
        connection_label: z.string().nullable(),
        /** Already granted at this level or above. */
        satisfied: z.boolean(),
      }),
    ),
  })
  .meta({ id: "AccessMatrixTemplate" });

export const accessRoutes = {
  "access.matrix": defineRoute({
    ...core,
    id: "access.matrix",
    method: "get",
    path: `${co}/access-matrix`,
    summary: "The access matrix the caller may see: agents × connections, each cell a level",
    access: "member",
    params: CompanyParams,
    query: AccessMatrixQuery,
    response: { status: 200, schema: AccessMatrix },
    errors: [],
    emits: [],
  }),
  "access.apply": defineRoute({
    ...core,
    id: "access.apply",
    method: "post",
    path: `${co}/access-matrix/apply`,
    summary: "Set access cells (one, a row, a column or a team) together",
    access: "member",
    params: CompanyParams,
    idempotency: "optional",
    body: AccessMatrixApply,
    response: { status: 200, schema: AccessMatrixApplyResult },
    errors: ["grant_not_allowed", "act_mode_unavailable"],
    emits: ["connection.grant_changed"],
  }),
  "access.template": defineRoute({
    ...core,
    id: "access.template",
    method: "get",
    path: `${co}/access-matrix/template/{id}`,
    summary: "What a DE's template asks for, matched to the company's connections, to confirm",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AccessMatrixTemplate },
    errors: [],
    emits: [],
  }),
} as const;
